import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { ShoppingCart, Menu, X, User, LogIn, ChevronDown, ExternalLink } from "lucide-react";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";

const Header = () => {
  const { t, cartCount, lang, setLangWithCurrency, currency, setCurrency, settings, customer } = useApp();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminArea = location.pathname.startsWith("/admin");

  useEffect(() => {
    api.get("/categories").then((r) => setCategories((r.data || []).filter((c) => c.active))).catch(() => {});
  }, []);

  const parents = categories.filter((c) => !c.parent_slug).sort((a, b) => a.order - b.order);
  const childrenOf = (slug) => categories.filter((c) => c.parent_slug === slug).sort((a, b) => a.order - b.order);

  const linkCls = ({ isActive }) =>
    `font-sans text-sm tracking-wide transition-colors ${isActive ? "text-holy-red" : "text-foreground hover:text-holy-red"}`;

  return (
    <header className="sticky top-0 z-50 bg-ivory/85 backdrop-blur-xl border-b border-border/60">
      {!isAdminArea && (
        <div className="bg-holy-red text-ivory text-center py-1.5 text-xs tracking-[0.18em] uppercase">
          ✦ {t("free_shipping_promo")} ✦
        </div>
      )}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-6">
        <Link to={isAdminArea ? "/admin" : "/"} className="flex items-center gap-3" data-testid="logo-link">
          {settings?.logo_url && (
            <img src={settings.logo_url} alt="Holy Flowy" className="h-12 w-12 rounded-full object-cover ring-1 ring-border" />
          )}
          <div className="leading-none">
            <div className="font-serif text-2xl text-holy-red tracking-tight flex items-center gap-2">
              Holy Flowy
              {isAdminArea && (
                <span
                  data-testid="admin-badge"
                  className="inline-flex items-center gap-1 text-[10px] font-sans tracking-widest uppercase px-2 py-0.5 rounded-full font-semibold bg-holy-red/10 text-holy-red"
                >
                  Admin
                </span>
              )}
            </div>
            <div className="font-sans text-[10px] tracking-[0.3em] uppercase text-holy-gold-dark mt-0.5">
              {isAdminArea ? "Dashboard Pengelolaan" : "Handmade Christmas"}
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {isAdminArea ? null : settings?.nav_links && settings.nav_links.filter((l) => l.active).length > 0 ? (
            settings.nav_links.filter((l) => l.active).sort((a, b) => a.order - b.order).map((l, i) => (
              <NavLink key={i} to={l.url} end={l.url === "/"} className={linkCls} data-testid={`nav-custom-${i}`}>
                {lang === "id" ? l.label_id : l.label_en}
              </NavLink>
            ))
          ) : (
            <>
              <NavLink to="/" end className={linkCls} data-testid="nav-home">{t("nav_home")}</NavLink>
              <div
                className="relative"
                onMouseEnter={() => setCatOpen(true)}
                onMouseLeave={() => setCatOpen(false)}
                data-testid="nav-shop-wrapper"
              >
                <NavLink
                  to="/shop"
                  className={({ isActive }) =>
                    `inline-flex items-center gap-1 font-sans text-sm tracking-wide transition-colors ${isActive ? "text-holy-red" : "text-foreground hover:text-holy-red"}`
                  }
                  data-testid="nav-shop"
                >
                  {t("nav_shop")} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${catOpen ? "rotate-180" : ""}`} />
                </NavLink>
                {catOpen && parents.length > 0 && (
                  <div
                    className="absolute left-1/2 -translate-x-1/2 top-full pt-3 z-50"
                    data-testid="nav-shop-dropdown"
                  >
                    <div className="bg-ivory rounded-2xl border border-border/70 shadow-card min-w-[260px]">
                      <Link
                        to="/shop"
                        onClick={() => setCatOpen(false)}
                        className="block px-5 py-3 text-sm font-serif text-holy-red hover:bg-cream rounded-t-2xl border-b border-border/50"
                        data-testid="dropdown-all"
                      >
                        ✦ {lang === "id" ? "Semua Produk" : "All Products"}
                      </Link>
                      <ul className="py-2 rounded-b-2xl">
                        {parents.map((p) => {
                          const subs = childrenOf(p.slug);
                          return (
                            <li key={p.id} className="group/parent relative">
                              <Link
                                to={`/shop?cat=${p.slug}`}
                                onClick={() => setCatOpen(false)}
                                data-testid={`dropdown-cat-${p.slug}`}
                                className="flex items-center justify-between gap-3 px-5 py-2.5 text-sm hover:bg-cream hover:text-holy-red transition-colors"
                              >
                                <span className="flex items-center gap-2">
                                  {p.image_url ? (
                                    <img src={p.image_url} alt="" className="w-6 h-6 rounded-full object-cover" />
                                  ) : (
                                    <span className="text-lg">{p.icon}</span>
                                  )}
                                  {lang === "id" ? p.name_id : p.name_en}
                                </span>
                                {subs.length > 0 && <ChevronDown className="w-3.5 h-3.5 -rotate-90" />}
                              </Link>
                              {subs.length > 0 && (
                                <div className="hidden group-hover/parent:block absolute left-full top-0 pl-2">
                                  <div className="bg-ivory rounded-2xl border border-border/70 shadow-card min-w-[200px] py-2">
                                    {subs.map((s) => (
                                      <Link
                                        key={s.id}
                                        to={`/shop?cat=${s.slug}`}
                                        onClick={() => setCatOpen(false)}
                                        data-testid={`dropdown-subcat-${s.slug}`}
                                        className="flex items-center gap-2 px-5 py-2.5 text-sm hover:bg-cream hover:text-holy-red transition-colors"
                                      >
                                        {s.image_url ? (
                                          <img src={s.image_url} alt="" className="w-5 h-5 rounded-full object-cover" />
                                        ) : (
                                          <span>{s.icon}</span>
                                        )}
                                        {lang === "id" ? s.name_id : s.name_en}
                                      </Link>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
              <NavLink to="/track" className={linkCls} data-testid="nav-track">{lang === "id" ? "Lacak" : "Track"}</NavLink>
              <NavLink to="/about" className={linkCls} data-testid="nav-about">{t("nav_about")}</NavLink>
            </>
          )}
        </nav>

        <div className="flex items-center gap-3">
          {isAdminArea ? (
            <Link
              to="/"
              data-testid="admin-view-store-btn"
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-semibold text-foreground hover:text-holy-red transition-colors border border-border rounded-full px-4 py-2 hover:border-holy-red"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Lihat Website
            </Link>
          ) : (
            <>
          <div className="hidden sm:flex items-center gap-1 text-xs">
            <button
              data-testid="lang-id"
              onClick={() => setLangWithCurrency("id")}
              className={`px-2 py-1 rounded-full transition-all ${lang === "id" ? "bg-holy-red text-ivory" : "text-foreground hover:bg-cream"}`}
            >ID</button>
            <span className="text-border">/</span>
            <button
              data-testid="lang-en"
              onClick={() => setLangWithCurrency("en")}
              className={`px-2 py-1 rounded-full transition-all ${lang === "en" ? "bg-holy-red text-ivory" : "text-foreground hover:bg-cream"}`}
            >EN</button>
          </div>

          <div className="hidden sm:flex items-center gap-1 text-xs border-l border-border pl-3">
            <button
              data-testid="currency-idr"
              onClick={() => setCurrency("IDR")}
              className={`px-2 py-1 rounded-full transition-all ${currency === "IDR" ? "bg-holy-green text-ivory" : "text-foreground hover:bg-cream"}`}
            >IDR</button>
            <span className="text-border">/</span>
            <button
              data-testid="currency-usd"
              onClick={() => setCurrency("USD")}
              className={`px-2 py-1 rounded-full transition-all ${currency === "USD" ? "bg-holy-green text-ivory" : "text-foreground hover:bg-cream"}`}
            >USD</button>
          </div>

          <button
            data-testid="cart-btn"
            onClick={() => navigate("/cart")}
            className="relative p-2 rounded-full hover:bg-cream transition-all"
            aria-label="Cart"
          >
            <ShoppingCart className="w-5 h-5" />
            {cartCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-holy-red text-ivory text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-semibold">
                {cartCount}
              </span>
            )}
          </button>

          {customer ? (
            <button
              data-testid="customer-account-btn"
              onClick={() => navigate("/account")}
              className="flex items-center gap-2 p-1 rounded-full hover:bg-cream transition-all"
              aria-label="My Account"
            >
              {customer.picture ? (
                <img src={customer.picture} alt="" className="w-8 h-8 rounded-full object-cover ring-2 ring-holy-red/30" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-holy-red/10 flex items-center justify-center"><User className="w-4 h-4 text-holy-red" /></div>
              )}
            </button>
          ) : (
            <button
              data-testid="customer-signin-btn"
              onClick={() => navigate("/login")}
              className="hidden sm:inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-foreground hover:text-holy-red transition-colors"
            >
              <LogIn className="w-3.5 h-3.5" /> {lang === "id" ? "Masuk" : "Sign In"}
            </button>
          )}

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden p-2 rounded-full hover:bg-cream"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
            </>
          )}
        </div>
      </div>

      {mobileOpen && !isAdminArea && (
        <div className="md:hidden border-t border-border/60 bg-ivory">
          <div className="px-5 py-4 flex flex-col gap-3">
            <NavLink to="/" end onClick={() => setMobileOpen(false)} className={linkCls} data-testid="m-nav-home">{t("nav_home")}</NavLink>
            <div className="border-b border-border/40 pb-2">
              <button
                onClick={() => setMobileCatOpen((v) => !v)}
                className="w-full flex items-center justify-between font-sans text-sm text-foreground"
                data-testid="m-nav-shop-toggle"
              >
                <NavLink
                  to="/shop"
                  onClick={(e) => { e.stopPropagation(); setMobileOpen(false); }}
                  className={linkCls}
                  data-testid="m-nav-shop"
                >
                  {t("nav_shop")}
                </NavLink>
                <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${mobileCatOpen ? "rotate-180" : ""}`} />
              </button>
              {mobileCatOpen && parents.length > 0 && (
                <ul className="mt-2 pl-3 space-y-1" data-testid="m-nav-shop-list">
                  {parents.map((p) => {
                    const subs = childrenOf(p.slug);
                    return (
                      <li key={p.id}>
                        <Link
                          to={`/shop?cat=${p.slug}`}
                          onClick={() => { setMobileOpen(false); setMobileCatOpen(false); }}
                          data-testid={`m-cat-${p.slug}`}
                          className="flex items-center gap-2 py-1.5 text-sm text-foreground hover:text-holy-red"
                        >
                          {p.image_url ? (
                            <img src={p.image_url} alt="" className="w-5 h-5 rounded-full object-cover" />
                          ) : (
                            <span>{p.icon}</span>
                          )}
                          {lang === "id" ? p.name_id : p.name_en}
                        </Link>
                        {subs.length > 0 && (
                          <ul className="pl-5 space-y-1">
                            {subs.map((s) => (
                              <li key={s.id}>
                                <Link
                                  to={`/shop?cat=${s.slug}`}
                                  onClick={() => { setMobileOpen(false); setMobileCatOpen(false); }}
                                  data-testid={`m-subcat-${s.slug}`}
                                  className="block py-1 text-xs text-muted-foreground hover:text-holy-red"
                                >
                                  <span className="mr-1">{s.icon}</span> {lang === "id" ? s.name_id : s.name_en}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <NavLink to="/track" onClick={() => setMobileOpen(false)} className={linkCls} data-testid="m-nav-track">{lang === "id" ? "Lacak" : "Track"}</NavLink>
            <NavLink to="/about" onClick={() => setMobileOpen(false)} className={linkCls} data-testid="m-nav-about">{t("nav_about")}</NavLink>
            {customer ? (
              <NavLink to="/account" onClick={() => setMobileOpen(false)} className={linkCls} data-testid="m-nav-account">{lang === "id" ? "Akun Saya" : "My Account"}</NavLink>
            ) : (
              <button onClick={() => { setMobileOpen(false); navigate("/login"); }} className="text-left font-sans text-sm text-foreground hover:text-holy-red" data-testid="m-customer-signin">
                {lang === "id" ? "Masuk / Daftar" : "Sign In / Register"}
              </button>
            )}
            <div className="flex gap-2 pt-2 border-t border-border/60">
              <button onClick={() => setLangWithCurrency("id")} className={`px-3 py-1.5 rounded-full text-xs ${lang === "id" ? "bg-holy-red text-ivory" : "bg-cream"}`}>ID</button>
              <button onClick={() => setLangWithCurrency("en")} className={`px-3 py-1.5 rounded-full text-xs ${lang === "en" ? "bg-holy-red text-ivory" : "bg-cream"}`}>EN</button>
              <button onClick={() => setCurrency("IDR")} className={`px-3 py-1.5 rounded-full text-xs ${currency === "IDR" ? "bg-holy-green text-ivory" : "bg-cream"}`}>IDR</button>
              <button onClick={() => setCurrency("USD")} className={`px-3 py-1.5 rounded-full text-xs ${currency === "USD" ? "bg-holy-green text-ivory" : "bg-cream"}`}>USD</button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
